import { useState, useEffect } from 'react'
import type { Deadline } from '../lib/api'

interface DeadlinesViewProps {
  deadlines: Deadline[]
  loading: boolean
}

function getTimeRemaining(targetDate: string) {
  const target = new Date(targetDate + 'T23:59:59')
  const now = new Date()
  const diff = target.getTime() - now.getTime()
  
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0, isPast: true }
  
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000),
    total: diff,
    isPast: false,
  }
}

function CountdownUnit({ value, label }: { value: number; label: string }) {
  return (
    <div className="text-center">
      <div className="glass rounded-lg px-3 py-2 min-w-[52px]">
        <p className="text-xl font-bold tabular-nums">{String(value).padStart(2, '0')}</p>
      </div>
      <p className="text-[10px] text-muted mt-1 uppercase tracking-wider">{label}</p>
    </div>
  )
}

function DeadlineCard({ deadline }: { deadline: Deadline }) {
  const [time, setTime] = useState(() => getTimeRemaining(deadline.target_date))
  
  useEffect(() => {
    const interval = setInterval(() => {
      setTime(getTimeRemaining(deadline.target_date))
    }, 1000)
    return () => clearInterval(interval)
  }, [deadline.target_date])

  const urgency = time.days <= 3 ? 'danger' : time.days <= 7 ? 'warning' : 'accent-light'
  const totalDays = Math.ceil((new Date(deadline.target_date + 'T23:59:59').getTime() - new Date(deadline.created_at).getTime()) / (1000 * 60 * 60 * 24))
  const elapsedDays = totalDays - time.days
  const progress = totalDays > 0 ? Math.min(100, (elapsedDays / totalDays) * 100) : 100

  return (
    <div className="glass rounded-xl p-4 animate-slide-up">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xl">{deadline.emoji}</span>
          <div>
            <h3 className="font-semibold text-sm">{deadline.name}</h3>
            <p className="text-xs text-muted">{deadline.description}</p>
          </div>
        </div>
        {time.isPast ? (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-muted/20 text-muted">Passed</span>
        ) : (
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium bg-${urgency}/15 text-${urgency}`}>
            {new Date(deadline.target_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        )}
      </div>

      {!time.isPast ? (
        <>
          <div className="flex justify-center gap-2 my-4">
            <CountdownUnit value={time.days} label="Days" />
            <div className="flex items-start pt-2 text-xl font-bold text-muted">:</div>
            <CountdownUnit value={time.hours} label="Hours" />
            <div className="flex items-start pt-2 text-xl font-bold text-muted">:</div>
            <CountdownUnit value={time.minutes} label="Min" />
            <div className="flex items-start pt-2 text-xl font-bold text-muted">:</div>
            <CountdownUnit value={time.seconds} label="Sec" />
          </div>
          
          {/* Progress bar */}
          <div className="mt-2">
            <div className="flex justify-between text-[10px] text-muted mb-1">
              <span>Time elapsed</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-white/5 rounded-full h-1.5">
              <div 
                className="rounded-full h-1.5 transition-all"
                style={{ 
                  width: `${progress}%`,
                  backgroundColor: deadline.color || '#6366f1'
                }}
              />
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-4">
          <p className="text-2xl mb-1">⏰</p>
          <p className="text-sm text-muted">Deadline has passed</p>
        </div>
      )}
    </div>
  )
}

export function DeadlinesView({ deadlines, loading }: DeadlinesViewProps) {
  if (loading) {
    return (
      <div className="px-4 space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="glass rounded-xl h-[180px] animate-pulse" />
        ))}
      </div>
    )
  }

  // Sort: upcoming first, then past
  const sorted = [...deadlines].sort((a, b) => {
    const aTime = getTimeRemaining(a.target_date)
    const bTime = getTimeRemaining(b.target_date)
    if (aTime.isPast && !bTime.isPast) return 1
    if (!aTime.isPast && bTime.isPast) return -1
    return new Date(a.target_date).getTime() - new Date(b.target_date).getTime()
  })

  return (
    <div className="px-4 space-y-3">
      <h2 className="text-lg font-bold flex items-center gap-2">
        <span>⏰</span> Deadlines
      </h2>
      {sorted.map((deadline, i) => (
        <div key={deadline.id} style={{ animationDelay: `${i * 80}ms` }}>
          <DeadlineCard deadline={deadline} />
        </div>
      ))}
      {sorted.length === 0 && (
        <div className="text-center py-8">
          <p className="text-3xl mb-2">🧘</p>
          <p className="text-sm text-muted">No deadlines. Enjoy the calm.</p>
        </div>
      )}
    </div>
  )
}
